/**
 * The one thing on the dispatch board that must survive a dead zone.
 *
 * Drivers work where signal does not. A wait clock stopped at a pad gate cannot
 * be lost to a missing bar, because the timestamp IS the money: an hour of
 * detention is $75, and it is priced from the moment the driver tapped, not the
 * moment their phone found LTE again. So a stop taken offline is stored with the
 * time it actually happened and replayed later, and the server honours that
 * stamp inside a validated window.
 *
 * WHAT IS DELIBERATELY NOT QUEUED, and why:
 *
 *   - CLAIM. A claim is a race against every other driver. Replaying one two
 *     hours later would take a load this driver has long since driven past, and
 *     may already belong to somebody else. It has to fail loudly, now.
 *   - DELIVERED. Marking delivered emails the customer to confirm and releases
 *     the payout. Sending that late is worse than sending it when signal
 *     returns — the driver is standing there and can tap again.
 *   - ARRIVED. A position fix is only true where it was taken. Replaying one
 *     from a parking lot 40 miles away would trip a geofence that never
 *     happened and text a site contact who is still waiting.
 *
 * localStorage, not IndexedDB: this holds a handful of tiny records and has to
 * survive a hard app kill. offlineQueue.ts is not reused — it is a FormData
 * pipeline hard-wired to /api/submissions and shares nothing with this.
 */

const KEY = 'zia_dispatch_offline_v1';
/** Past this the stamp is a stale queue, not a wait — matches the server. */
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

export type QueuedDetention = {
  id: string;
  jobId: string;
  end: 'pickup' | 'drop';
  action: 'start' | 'stop';
  /** When the driver actually tapped. The whole reason this queue exists. */
  atISO: string;
  tries: number;
};

function read(): QueuedDetention[] {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as QueuedDetention[]) : [];
  } catch {
    return [];
  }
}

function write(items: QueuedDetention[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch { /* a full disk must not break the clock the driver can still see */ }
}

/** Drop anything the server would reject anyway, so it cannot retry forever. */
function fresh(items: QueuedDetention[], nowMs: number): QueuedDetention[] {
  return items.filter((i) => {
    const t = Date.parse(i.atISO);
    return Number.isFinite(t) && nowMs - t < MAX_AGE_MS && i.tries < 8;
  });
}

export function queueDetention(
  entry: Omit<QueuedDetention, 'id' | 'tries'>,
): QueuedDetention {
  const item: QueuedDetention = {
    ...entry,
    id: `${entry.jobId}:${entry.end}:${entry.action}:${entry.atISO}`,
    tries: 0,
  };
  const items = fresh(read(), Date.now());
  // Same tap twice (a double-press, a re-queue after a failed replay) is one
  // event. The id carries the timestamp, so genuinely distinct taps survive.
  write([...items.filter((i) => i.id !== item.id), item]);
  return item;
}

export function pendingDetention(): QueuedDetention[] {
  return fresh(read(), Date.now());
}

export function pendingDetentionCount(): number {
  return pendingDetention().length;
}

/**
 * Replay everything we are holding.
 *
 * `send` returns true when the server accepted it. Anything that fails stays
 * queued with a bumped try count; anything the server accepted leaves. Never
 * throws — a replay that blows up must not take down the screen that called it.
 */
type FlushResult = { sent: number; kept: number };
let activeFlush: Promise<FlushResult> | null = null;

export function flushDetention(
  send: (item: QueuedDetention) => Promise<boolean>,
): Promise<FlushResult> {
  // Online and focus can fire together. Share the replay so the same event
  // isn't sent twice or charged two retry attempts by this app instance.
  if (!activeFlush) {
    activeFlush = flushBatch(send).finally(() => { activeFlush = null; });
  }
  return activeFlush;
}

async function flushBatch(
  send: (item: QueuedDetention) => Promise<boolean>,
): Promise<FlushResult> {
  const items = fresh(read(), Date.now());
  if (items.length === 0) return { sent: 0, kept: 0 };

  let sent = 0;
  for (const item of items) {
    let ok = false;
    try {
      ok = await send(item);
    } catch {
      ok = false;
    }
    if (ok) sent += 1;
    // A driver can save another tap while send() is pending. Reconcile only
    // this event against current storage, preserving new taps and committing
    // each acknowledgement before awaiting the next network request.
    write(fresh(read(), Date.now()).flatMap((current) => {
      if (current.id !== item.id) return [current];
      return ok ? [] : [{ ...current, tries: current.tries + 1 }];
    }));
  }
  return { sent, kept: pendingDetentionCount() };
}

export function clearDetentionQueue(): void {
  write([]);
}
